/**
 * CSS de listes imbriquées partagé entre :
 *   - l'éditeur Tiptap (`RichTextEditor.tsx`)
 *   - l'aperçu d'email (`EmailPreview.tsx`)
 *   - les emails effectivement envoyés / exportés (`emailHtmlTemplate.ts`)
 *
 * Garantit que le rendu des `<ul>`/`<ol>` (marqueurs cyclés sur 12 niveaux,
 * imbrications mixtes, espacement) est strictement identique partout.
 *
 * Le sélecteur racine est paramétrable pour scoper les règles soit à la
 * surface d'édition ProseMirror, soit à la zone de rendu de l'email.
 */

export function buildEmailListCss(rootSelector: string, paragraphSpacingPx = 8): string {
  const ulCycle = ["disc", "circle", "square"];
  const olCycle = ["decimal", "lower-alpha", "lower-roman", "upper-alpha", "upper-roman"];
  const lines: string[] = [];

  // Règles de base (réinitialisent le reset Tailwind `list-style: none`).
  lines.push(
    `${rootSelector} ul, ${rootSelector} ol { padding-left: 1.6em; margin: ${paragraphSpacingPx}px 0; }`,
    `${rootSelector} li { margin: 2px 0; }`,
    `${rootSelector} li > p { margin: 0; }`,
    `${rootSelector} li::marker { color: inherit; }`,
    `${rootSelector} li ul, ${rootSelector} li ol { margin: 2px 0; padding-left: 1.4em; }`,
  );

  // Cyclage jusqu'à 12 niveaux, indépendant de l'alternance ul/ol au-dessus.
  for (let depth = 1; depth <= 12; depth++) {
    const ancestors = depth > 1 ? Array(depth - 1).fill(":is(ul,ol)").join(" ") + " " : "";
    const ulMarker = ulCycle[(depth - 1) % ulCycle.length];
    const olMarker = olCycle[(depth - 1) % olCycle.length];
    lines.push(`${rootSelector} ${ancestors}ul { list-style: ${ulMarker} outside; }`);
    lines.push(`${rootSelector} ${ancestors}ol { list-style: ${olMarker} outside; }`);
  }

  return lines.join("\n");
}

/**
 * Bloc `<style>` prêt à injecter dans le `<head>` d'un email exporté ou dans
 * un conteneur React. Utilise une classe racine `email-body` qu'il suffit
 * d'appliquer au wrapper du corps de l'email.
 */
export function emailListStyleBlock(paragraphSpacingPx = 8): string {
  return `<style>${buildEmailListCss(".email-body", paragraphSpacingPx)}</style>`;
}
